import { BookingNumber } from 'components/tables/utils';
import { Booking, Role } from 'types';
import { T_DataTableColumn } from 'types/data-table';

interface BookingColumnType {
  bookingDetails: T_DataTableColumn<Booking, 'bookingDetails'>;
  bookingNumber: T_DataTableColumn<Booking, 'text'>;
  bookingTemperature: T_DataTableColumn<Booking, 'number'>;
  createdBy: T_DataTableColumn<Booking, 'text'>;
  createdDate: T_DataTableColumn<Booking, 'dateTime'>;
  customer: T_DataTableColumn<Booking, 'customer'>;
  customerCode: T_DataTableColumn<Booking, 'text'>;
  customerFullName: T_DataTableColumn<Booking, 'text'>;
  customerLocation: T_DataTableColumn<Booking, 'text'>;
  customerName: T_DataTableColumn<Booking, 'text'>;
  expirationDate: T_DataTableColumn<Booking, 'dateTime'>;
  humidity: T_DataTableColumn<Booking, 'number'>;
  id: T_DataTableColumn<Booking, 'number'>;
  lastUpdatedBy: T_DataTableColumn<Booking, 'text'>;
  lastUpdatedDate: T_DataTableColumn<Booking, 'dateTime'>;
  location: T_DataTableColumn<Booking, 'location'>;
  notes: T_DataTableColumn<Booking, 'text'>;
  railyard: T_DataTableColumn<Booking, 'text'>;
  requiresGenset: T_DataTableColumn<Booking, 'boolean'>;
  startDate: T_DataTableColumn<Booking, 'dateTime'>;
  truckingCompanies: T_DataTableColumn<Booking, 'truckingCompanies'>;
  ventilation: T_DataTableColumn<Booking, 'text'>;
  vesselLloydsNumber: T_DataTableColumn<Booking, 'text'>;
  vesselName: T_DataTableColumn<Booking, 'text'>;
  vesselVoyage: T_DataTableColumn<Booking, 'text'>;
  /* computed fields */
  count: T_DataTableColumn<Booking, 'number'>;
  sizeType: T_DataTableColumn<Booking, 'text'>;
  reserved: T_DataTableColumn<Booking, 'number'>;
  received: T_DataTableColumn<Booking, 'number'>;
  delivered: T_DataTableColumn<Booking, 'number'>;
  anyTruckingCompany: T_DataTableColumn<Booking, 'boolean'>;
}

export const bookingDataColumns: { [P in keyof BookingColumnType]: BookingColumnType[P] }[keyof BookingColumnType][] = [
  { width: 112, field: 'id', label: 'RECORD', type: 'number', category: 'common' },
  {
    width: 160,
    field: 'bookingNumber',
    label: 'BOOKING',
    type: 'text',
    category: 'common',
    renderCell: (_, row) => <BookingNumber booking={row} ADMIN BILLING_ADMIN SENIOR_BOOKING BOOKING CUSTOMER />
  },
  {
    width: 144,
    field: 'customerName',
    label: 'ACCOUNT NAME',
    sortable: false,
    type: 'text',
    category: 'common'
  },
  {
    width: 144,
    field: 'customerFullName',
    label: 'FULL ACCOUNT NAME',
    sortable: false,
    type: 'text',
    category: 'common'
  },
  {
    width: 144,
    field: 'customerCode',
    label: 'ACCOUNT CODE',
    sortable: false,
    type: 'text',
    category: 'common'
  },
  {
    width: 144,
    field: 'customerLocation',
    label: 'ACCOUNT LOCATION',
    sortable: false,
    type: 'text',
    category: 'common'
  },
  {
    width: 160,
    field: 'location',
    label: 'LOCATION',
    type: 'location',
    getValueLabel: location => location?.name,
    category: 'common'
  },
  {
    width: 128,
    field: 'createdBy',
    label: 'USER',
    type: 'text',
    allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING],
    category: 'common'
  },
  {
    width: 128,
    field: 'startDate',
    label: 'START DATE',
    type: 'dateTime',
    dateFormat: 'l',
    category: 'common'
  },
  {
    width: 160,
    field: 'expirationDate',
    label: 'EXPIRATION',
    type: 'dateTime',
    dateFormat: 'l',
    category: 'common'
  },
  {
    width: 104,
    field: 'count',
    label: 'COUNT',
    type: 'number',
    filterable: false,
    skipQuickSearch: true,
    sortable: false,
    category: 'common'
  },
  {
    width: 120,
    field: 'sizeType',
    label: 'SIZE TYPE',
    type: 'text',
    sortable: false,
    category: 'common'
  },
  { width: 144, field: 'notes', label: 'NOTES', type: 'text', category: 'common' },
  {
    width: 144,
    field: 'reserved',
    label: 'RESERVED',
    type: 'number',
    filterable: false,
    skipQuickSearch: true,
    sortable: false,
    category: 'common'
  },
  {
    width: 144,
    field: 'received',
    label: 'RECEIVED',
    type: 'number',
    filterable: false,
    skipQuickSearch: true,
    sortable: false,
    category: 'common'
  },
  {
    width: 128,
    field: 'delivered',
    label: 'DELIVERED',
    type: 'number',
    filterable: false,
    skipQuickSearch: true,
    sortable: false,
    category: 'common'
  },
  { width: 112, field: 'requiresGenset', label: 'GENSET', type: 'boolean', category: 'common' },
  { width: 152, field: 'bookingTemperature', label: 'TEMPERATURE', type: 'number', category: 'common' },
  { width: 128, field: 'humidity', label: 'HUMIDITY', type: 'number', category: 'common' },
  { width: 144, field: 'ventilation', label: 'VENTILATION', type: 'number', category: 'common' },
  { width: 144, field: 'railyard', label: 'RAILYARD', type: 'text', category: 'common' },
  { width: 104, field: 'vesselLloydsNumber', label: 'LLOYDS', category: 'common' },
  { width: 144, field: 'vesselName', label: 'VESSEL', category: 'common' },
  { width: 104, field: 'vesselVoyage', label: 'VOYAGE', category: 'common' },
  {
    width: 112,
    field: 'anyTruckingCompany',
    label: 'ANY TRUCKING CO',
    type: 'boolean',
    // getValue: booking => !booking?.truckingCompanies?.length,
    filterable: false,
    skipQuickSearch: true,
    sortable: false,
    category: 'common'
  }
];

export const defaultBookingColumns: Array<keyof BookingColumnType> = [
  'bookingNumber',
  'customerName',
  'createdDate',
  'expirationDate',
  'count',
  'sizeType',
  'notes',
  'received',
  'delivered'
];
